import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { findProductByIdWithBranch } from '../../actions';
import styles from './styles.module.css';

const getVariantId = (variant) => variant?.id || null;
const hasValue = (value) => value !== null && value !== undefined && value !== '';
const PLACEHOLDER_IMAGE =
  "data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 320 320'%3E%3Cdefs%3E%3ClinearGradient id='g' x1='0' y1='0' x2='1' y2='1'%3E%3Cstop offset='0%25' stop-color='%23cbd5e1'/%3E%3Cstop offset='100%25' stop-color='%2394a3b8'/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='320' height='320' fill='url(%23g)'/%3E%3Cpath d='M0 230l82-78 54 44 44-34 70 68 70-60v150H0z' fill='%2364748b' opacity='.55'/%3E%3Ctext x='160' y='170' text-anchor='middle' font-family='Arial,sans-serif' font-size='24' font-weight='700' fill='%23ffffff'%3ENo Image%3C/text%3E%3C/svg%3E";

const normalizeProduct = (product, fallbackVariants = []) => {
  const meta = product?.filteredProductMeta || {};
  const stockFromMeta = meta?.stockAmount;
  const resolvedPrice =
    product?.price ??
    product?.nonStockPrice ??
    meta?.nonStockPrice ??
    null;
  const stockamount =
    product?.stockamount != null && product?.stockamount !== ''
      ? product.stockamount
      : (stockFromMeta != null && stockFromMeta !== '' ? stockFromMeta : 0);

  return {
    ...product,
    id: product?.id ?? null,
    code: product?.code ?? null,
    barcode: product?.barcode ?? null,
    price: resolvedPrice,
    stockamount,
    productType: product?.productType ?? product?.type ?? null,
    type: product?.type ?? product?.productType ?? null,
    nonStockProductAvailability: product?.nonStockProductAvailability ?? null,
    variants:
      Array.isArray(product?.variants) && product.variants.length > 0
        ? product.variants
        : fallbackVariants,
  };
};

const toImageItems = (images) =>
  (images || [])
    .map((image, index) => ({
      id: image?.id || `image-${index}`,
      name: image?.name || `Image ${index + 1}`,
      src: image?.signedUrl || image?.imagePath || '',
    }))
    .filter((image) => image.src);

const formatPrice = (value) => {
  if (!hasValue(value)) return '-';
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) return '-';
  return `Rs. ${numberValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const ProductImagePanel = ({ productId, branchId, onClose }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedProductImages, setSelectedProductImages] = useState([]);

  const isOpen = productId != null;

  useEffect(() => {
    if (!productId) {
      setSelectedProduct(null);
      setSelectedProductImages([]);
      setActiveImageIndex(0);
      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        setIsLoading(true);
        setActiveImageIndex(0);

        const productResponse = await findProductByIdWithBranch(productId, branchId);
        let displayProduct = normalizeProduct(productResponse);

        const variants = Array.isArray(displayProduct?.variants) ? displayProduct.variants : [];
        const firstVariantId = getVariantId(variants[0]);
        const shouldOpenFirstVariant =
          (displayProduct?.isVariantAvailable === 1 || displayProduct?.isVariantAvailable === true) &&
          !displayProduct?.parentProductId &&
          firstVariantId;

        if (shouldOpenFirstVariant) {
          const firstVariant = variants.find((variant) => getVariantId(variant) === firstVariantId) || variants[0];
          displayProduct = normalizeProduct(firstVariant, variants);
        }

        if (cancelled) return;
        setSelectedProduct(displayProduct);
        setSelectedProductImages(Array.isArray(displayProduct?.productImages) ? displayProduct.productImages : []);
      } catch {
        if (cancelled) return;
        setSelectedProduct(null);
        setSelectedProductImages([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [productId, branchId]);

  const handleVariantSelect = (variantId) => {
    if (!variantId) return;
    const variants = Array.isArray(selectedProduct?.variants) ? selectedProduct.variants : [];
    const selectedVariant = variants.find((variant) => getVariantId(variant) === variantId);
    if (!selectedVariant) return;

    const displayVariant = normalizeProduct(selectedVariant, variants);
    setActiveImageIndex(0);
    setSelectedProduct(displayVariant);
    setSelectedProductImages(Array.isArray(displayVariant?.productImages) ? displayVariant.productImages : []);
  };

  const imageItems = useMemo(() => toImageItems(selectedProductImages), [selectedProductImages]);
  const activeImage = imageItems[activeImageIndex] || null;

  const productCode = selectedProduct?.code || '-';
  const productBarcode = selectedProduct?.barcode || '-';
  const productPrice = formatPrice(selectedProduct?.price);
  const variants = Array.isArray(selectedProduct?.variants) ? selectedProduct.variants : [];
  const hasVariants = variants.length > 0;
  const selectedVariantName = selectedProduct?.variantName;

  const stockCountNumber = Number(selectedProduct?.stockamount);
  const stockCount = Number.isFinite(stockCountNumber) ? stockCountNumber : 0;
  const isInStock = stockCount > 0;

  const productType = String(selectedProduct?.productType || selectedProduct?.type || '').toUpperCase();
  const isNonStock = productType === 'NON_STOCK';
  const availabilityValue = selectedProduct?.nonStockProductAvailability;
  const hasAvailability = hasValue(availabilityValue);
  const isAvailable = availabilityValue === true || availabilityValue === 1 || availabilityValue === '1';

  const statusLabel = isNonStock
    ? (hasAvailability ? (isAvailable ? 'Available' : 'Unavailable') : '-')
    : (isInStock ? 'In Stock' : 'Out of Stock');
  const isPositiveStatus = isNonStock ? isAvailable : isInStock;

  return (
    <>
      {isOpen && (
        <button
          type="button"
          className={styles.backdrop}
          onClick={onClose}
          aria-label="Close image preview"
        />
      )}

      <aside className={`${styles.panel} ${isOpen ? styles.panelOpen : ''}`} aria-hidden={!isOpen}>
        <div className={styles.header}>
          <div className={styles.meta}>
            <div className={styles.category}>{selectedProduct?.categoryname || 'Product'}</div>
            <h3 className={styles.title}>{selectedProduct?.name || 'Product Images'}</h3>
          </div>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close image panel"
          >
            ×
          </button>
        </div>

        {isLoading ? (
          <div className={styles.loading}>Loading images…</div>
        ) : (
          <div className={styles.body}>
            <div className={styles.imageMainWrap}>
              {activeImage ? (
                <img src={activeImage.src} alt={activeImage.name || 'Product'} className={styles.imageMain} />
              ) : (
                <img src={PLACEHOLDER_IMAGE} alt="No product image" className={styles.imagePlaceholder} />
              )}
            </div>

            {imageItems.length > 1 && (
              <div className={styles.thumbList}>
                {imageItems.map((image, index) => (
                  <button
                    key={image.id}
                    type="button"
                    className={`${styles.thumbBtn} ${index === activeImageIndex ? styles.thumbBtnActive : ''}`}
                    onClick={() => setActiveImageIndex(index)}
                  >
                    <img src={image.src} alt={image.name} className={styles.thumb} />
                  </button>
                ))}
              </div>
            )}

            <div className={styles.info}>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Code</span>
                <span className={styles.infoValue}>{productCode}</span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Barcode</span>
                <span className={styles.infoValue}>{productBarcode}</span>
              </div>

              <div className={styles.stockStatusWrap}>
                <span className={`${styles.stockStatus} ${isPositiveStatus ? styles.stockStatusIn : styles.stockStatusOut}`}>
                  {statusLabel}
                </span>
              </div>

              <div className={styles.priceRow}>
                <span className={styles.priceRowLabel}>Price</span>
                <span className={styles.price}>{productPrice}</span>
              </div>

              {hasVariants && (
                <div className={styles.variantsBlock}>
                  <div className={styles.variantsLabel}>Variants</div>
                  <div className={styles.variantList}>
                    {variants.map((variant) => {
                      const variantId = getVariantId(variant);
                      const variantName = variant?.variantName || '-';
                      const isActive = selectedVariantName
                        ? selectedVariantName === variantName
                        : variantId === selectedProduct?.id;
                      return (
                        <button
                          key={variantId || variantName}
                          type="button"
                          className={`${styles.variantChip} ${isActive ? styles.variantChipActive : ''}`}
                          onClick={() => handleVariantSelect(variantId)}
                        >
                          {variantName}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {!isNonStock && (
                <div className={styles.availableLine}>
                  {`${stockCount} available in stock`}
                </div>
              )}
            </div>
          </div>
        )}
      </aside>
    </>
  );
};

ProductImagePanel.propTypes = {
  productId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  branchId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClose: PropTypes.func.isRequired,
};

ProductImagePanel.defaultProps = {
  productId: null,
  branchId: null,
};

export default ProductImagePanel;
